import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { GitFileChange, GitService } from '@y42/git';
import { MonacoDiffEditorDirective } from '@y42/monaco-editor';
import { wait } from '@y42/util/wait';
import { GetContextMenuItemsParams } from 'ag-grid-community';
import { SplitAreaDirective } from 'angular-split';
import { GitDiffTreeComponent } from '../../git-diff-tree/git-diff-tree.component';

@Component({
  selector: 'y42-git-commit-preview',
  templateUrl: './git-commit-preview.component.html',
  styleUrls: ['./git-commit-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitCommitPreviewComponent implements OnChanges {
  constructor(private readonly gitService: GitService) {}

  @Input() changes: GitFileChange[] = [];

  @Input() selectedItems: GitFileChange[] = [];

  @Input() allowCheckboxSelection = true;

  @Input() disableFileTreeContextMenu = false;

  @Input() allowDiscardChanges = true;

  @Output() selectedItemsChange = new EventEmitter<GitFileChange[]>();

  @ViewChild('treeArea', { read: SplitAreaDirective }) treeArea!: SplitAreaDirective;
  @ViewChild('diffArea', { read: SplitAreaDirective }) diffArea!: SplitAreaDirective;
  @ViewChild(MonacoDiffEditorDirective) editor!: MonacoDiffEditorDirective;
  @ViewChild(GitDiffTreeComponent) diffTree!: MonacoDiffEditorDirective;

  activeItem: GitFileChange | null = null;

  readonly viewSelectorFC = new FormControl<'source' | 'ui'>('source', { nonNullable: true });

  ngOnChanges(changes: SimpleChanges) {
    if (changes.changes) {
      this.setActiveFile(this.changes[0] ?? null);
    }
  }

  setActiveFile(file: GitFileChange | null) {
    this.activeItem = file;
  }

  getContextMenuItems = (params: GetContextMenuItemsParams) => {
    const selectedRows = params.api.getSelectedRows();
    if (!selectedRows.length) {
      return [];
    }

    if (!this.allowDiscardChanges) {
      return [];
    }

    return [
      {
        name: 'Discard selected changes...',
        action: async () => {
          await this.gitService.discardChanges(selectedRows.map(({ path }) => path));
        },
      },
    ];
  };

  async expandDiff() {
    this.expandArea('diff');
  }

  async expandTree() {
    this.expandArea('tree');
  }

  private async expandArea(area: 'diff' | 'tree') {
    const expandedSplitArea = area === 'diff' ? this.diffArea : this.treeArea;
    const collapsedSplitArea = area === 'diff' ? this.treeArea : this.diffArea;

    collapsedSplitArea.size = 20;
    expandedSplitArea.size = 80;

    const AS_SPLIT_TRANSITION_DURATION = 300;
    await wait(AS_SPLIT_TRANSITION_DURATION);
    this.editor.updateLayout();
    this.diffTree.updateLayout();
  }
}
